/* eslint-disable @typescript-eslint/naming-convention */
import Container from '@brands/Components/Container/Container';
import MuiPhoneInput from '@brands/Components/Inputs/MuiTelInput/MuiTelInput';
import CustomTextField from '@brands/Components/Inputs/MuiTextField/TextField';
import OldPhoneInput from '@brands/Components/Inputs/PhoneInput/PhoneInput';
import { Loading } from '@brands/Components/LoadingSpinner/Loading';
import UnsavedChangesModal from '@brands/Dashboard/Dashboard/Components/UnsavedChangesModal/UnsavedChangesModal';
import AddressSection from '@brands/Dashboard/Dashboard/SuperAdmin/Components/AddressSection/AddressSection';
import ChangePasswordModal from '@brands/Dashboard/Sidenav/SidenavModals/ChangePasswordModal/ChangePasswordModal';
import { useUnsavedChangesWarning } from '@brands/hooks/useUnsavedChangesWarning';
import { useDivisionScopeActor } from '@brands/hooks/useDivisionScopeActor';
import { getUserAvatar } from '@brands/services/identity/getUserAvatar';
import { getUserById } from '@brands/services/identity/getUserById';
import { listAllClientTags } from '@brands/services/identity/listAllClientTags';
import { updateUserClientTag } from '@brands/services/identity/updateUserClientTag';
import { updateVerifiedEmail } from '@brands/services/identity/updateUserEmail';
import { mapProfileToMyProfileSubmit } from '@brands/store/actions/organizationAdminAction';
import { selectAuth } from '@brands/store/selectors/auth';
import { setUser } from '@brands/store/slices/authSlice';
import { UnifiedProfileSubmit } from '@brands/Utils/types';
import { validateAddress, ValidationModalInterface } from '@brands/Utils/validateAddress';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button as MuiButton, Chip } from '@mui/material';
import cn from 'classnames';
import { E164Number, parsePhoneNumber } from 'libphonenumber-js';
import { isEqual } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, UseFormGetValues, useWatch } from 'react-hook-form';
import { FiEdit } from 'react-icons/fi';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import BlueCircle from '../../../assets/icons/light-blue-circle.svg';
import GrayCircle from '../../../assets/icons/light-gray-circle.svg';
import Button from '../../../Components/Button/Button';
import TextField from '../../../Components/Inputs/TextField/TextField';
import ProfileImage from '../../../Components/ProfileImage/ProfileImage';
import { useAppDispatch, useAppSelector } from '../../../hooks/useReduxHook';
import { defaultUserValues } from '../../../services/cases/types/defaultUserValues';
import { RegisterMediaPayload } from '../../../services/communication/registersConversationMedia';
import { confirmAvatarMedia } from '../../../services/identity/confirmAvatarMedia';
import { createBasicInformation } from '../../../services/identity/createBasicInformation';
import { deleteAvatar } from '../../../services/identity/deleteAvatar';
import { getCurrentProfile } from '../../../services/identity/getCurrentProfile';
import { registerAvatarMedia } from '../../../services/identity/registerAvatarMedia';
import { saveBasicInformation } from '../../../services/identity/saveBasicInformation';
import { Address, BasicInfo, UserProfile, UserRoleName, ClientTag } from '../../../services/identity/types/UserProfile';
import { UpdateMe, updateMe } from '../../../services/identity/updateMe';
import { displayErrorDetails } from '../../../Utils/displayError';
import Sidenav from '../../Sidenav/Sidenav';
import caStyles from '../CareAdvisor/Sections/styles.module.scss';
import ModernReviewProfileHeader from '../Components/ModernReviewProfileHeader';
import ChangeUserStatusModal from '../Modal/ChangeUserStatusModal';
import ResetPasswordModal from '../Modal/ResetPasswordModal';
import { updateData } from '../Patient/utils/utilsFunctions';
import newProfileStyles from '../SuperAdmin/styles.module.scss';
import styles from '../styles.module.scss';
import { OrganizationAdminMyProfileSubmit } from './utils/types';
import { profileValidationSchema } from './utils/validationSchema';
import { ClientTagOption } from '../CareAdvisor/utils/types';
import MUISelectInput from '@brands/Components/Inputs/MuiSelectInput/MUISelectInput';
import MultiSelectInputMUI from '@brands/Components/Inputs/MuiMultiSelectInput/MultiSelectInputMUI';
import {
  ClientDivisionGroup,
  getAllowedDivisionIds,
  getClientDivisionOptionGroups,
  hasUnfilledRequiredDivisionGroup,
  scopeDivisionGroupsToClientAdmin,
} from '@brands/services/identity/clientDivisionOptions';
import { getUserClientTagRefs, toLockedSelectOptions } from '@brands/Utils/userClientTags';

type ReviewMyProfileProps = {
  reviewProfile?: boolean;
};

const MyProfile = ({ reviewProfile }: ReviewMyProfileProps): JSX.Element => {
  const userId = useParams().userId as string;
  const roleName = useParams().role as string;
  const { userInfo } = useAppSelector(selectAuth);
  const divisionScopeActor = useDivisionScopeActor();
  const [thisUser, setThisUser] = useState<UserProfile>(userInfo || defaultUserValues);
  const [image, setImage] = React.useState<string>();
  const [currentAvatarURL, setCurrentAvatarURL] = useState<string>('');
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPayload, setAvatarPayload] = useState<RegisterMediaPayload | null>(null);
  const [avatarAction, setAvatarAction] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [render, setRender] = useState(false);
  const [isEditProfile, setIsEditProfile] = useState(Boolean(reviewProfile));
  const dispatch = useAppDispatch();
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const [validationResponseId, setValidationResponseId] = useState('');
  const [resetPasswordConfirmation, setResetPasswordConfirmation] = useState<{ isOpen: boolean; userState: string }>({
    isOpen: false,
    userState: thisUser.state || '',
  });
  const [showValidationAddressModal, setShowValidationAddressModal] = useState<ValidationModalInterface>({
    isOpen: false,
    title: '',
    whatYouEntered: '',
    recommended: false,
    recommendedAddress: '',
    onClose: undefined,
    onConfirmChange: undefined,
    onKeepCurrentAddress: undefined,
  });
  const [changeUserStatusFlag, setChangeUserStatusFlag] = useState('');
  const [resetEmailSent, setResetEmailSent] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [allClientTags, setAllClientTags] = useState<ClientTag[]>([]);
  const [clientTagOptions, setClientTagOptions] = useState<ClientTagOption[]>([]);
  const formRef = useRef<HTMLFormElement>(null);
  const imageObjectUrlRef = useRef<string | null>(null);
  const disableButton = (): void => {
    setResetEmailSent(true);
  };
  const disableDeleteButton = (): void => {
    setThisUser({
      ...thisUser,
      status: 'Deactivated',
    });
  };
  const [myProfileForm, setMyProfileForm] = useState<OrganizationAdminMyProfileSubmit>(
    {} as OrganizationAdminMyProfileSubmit,
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    control,
  } = useForm<OrganizationAdminMyProfileSubmit>({
    mode: 'onChange',
    resolver: yupResolver(profileValidationSchema(reviewProfile)),
  });

  const watchedValues = useWatch({ control });
  const hasUnsavedChanges = useMemo(
    () =>
      render &&
      Object.keys(myProfileForm).length > 1 &&
      (!isEqual(myProfileForm, watchedValues) || avatarAction !== ''),
    [avatarAction, myProfileForm, render, watchedValues],
  );

  const { showModal, confirmNavigation, saveChanges, hideModal } = useUnsavedChangesWarning(hasUnsavedChanges, formRef);

  const getCurrentUser = async (id: string): Promise<void> => {
    try {
      setLoading(true);
      setRender(false);
      if (reviewProfile && userId) {
        const currentUser = await getUserById(Number(id));
        setRender(false);
        setThisUser(currentUser);
      }
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserDetails = async (): Promise<void> => {
    try {
      setLoading(true);
      setRender(false);
      const userDetails = await getCurrentProfile();
      setRender(false);
      setThisUser((prev) => ({
        ...userDetails,
        clients: userDetails.clients ?? prev.clients,
      }));
    } catch (error: unknown) {
      displayErrorDetails(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfile = (): void => {
    const mappedProfile = mapProfileToMyProfileSubmit(thisUser);
    setMyProfileForm({
      ...mappedProfile,
      organizations: mappedProfile.organizations,
      client_tag_ids: mappedProfile.client_tag_ids,
      client_division_ids: mappedProfile.client_division_ids,
    });
  };
  useEffect(() => {
    fetchProfile();
  }, [thisUser, clientTagOptions]);

  useEffect(() => {
    if (userId) {
      getCurrentUser(userId);
    } else {
      fetchUserDetails();
    }
  }, [userId]);

  const triggerProfileAvatar = React.useCallback((avatarUrl: string) => {
    setValue('profile_img', avatarUrl, { shouldValidate: true });
    setMyProfileForm({
      ...getValues(),
      profile_img: avatarUrl,
    });
  }, []);

  const onCloseConfirmationModal = (): void => {
    setShowValidationAddressModal({
      isOpen: false,
      title: '',
      whatYouEntered: '',
      recommended: false,
      recommendedAddress: '',
      onClose: undefined,
      onConfirmChange: undefined,
      onKeepCurrentAddress: undefined,
    });
  };
  const callSaveProfile = async (profileData: UnifiedProfileSubmit): Promise<void> => {
    setIsSavingProfile(true);
    try {
      if (avatarPayload && avatarFile && avatarAction === 'Upload') {
        const res = await registerAvatarMedia(avatarPayload);
        const mediaId = res.files[0].upload_id;
        await fetch(res.files[0].upload_url, {
          method: 'PUT',
          body: avatarFile,
          headers: { 'content-type': avatarFile.type },
        });
        await confirmAvatarMedia(mediaId, Number(thisUser?.id));
        const avatarUrl = getUserAvatar(Number(thisUser?.id));
        if (imageObjectUrlRef.current) {
          URL.revokeObjectURL(imageObjectUrlRef.current);
          imageObjectUrlRef.current = null;
        }
        setImage(avatarUrl);
        setCurrentAvatarURL(avatarUrl);
        triggerProfileAvatar(avatarUrl);
      } else if (avatarAction === 'Remove') {
        await deleteAvatar(thisUser?.id || 0);
        setImage(undefined);
        setCurrentAvatarURL('');
        triggerProfileAvatar('');
      }

      let updatedUser;

      const submittedClientTagIds = ((profileData as OrganizationAdminMyProfileSubmit).client_tag_ids || [])
        .map((id) => Number(id))
        .filter((n) => Number.isInteger(n) && n > 0);
      const submittedClientDivisionIds = ((profileData as OrganizationAdminMyProfileSubmit).client_division_ids || [])
        .map((id) => Number(id))
        .filter((n) => Number.isInteger(n) && n > 0);

      if (thisUser?.email !== (profileData as OrganizationAdminMyProfileSubmit).email) {
        updatedUser = await updateVerifiedEmail(thisUser?.id || 0, {
          email: (profileData as OrganizationAdminMyProfileSubmit).email as string,
        });
      }

      const dataWithUpdatedDOB = updateData(profileData as OrganizationAdminMyProfileSubmit);
      const { address1, address2, city, state, zip_code } = profileData;
      const userAddress: Address = { address1, address2, city, state, zip_code };
      updatedUser = await updateMe(
        {
          ...(profileData as UpdateMe),
          ...((profileData as OrganizationAdminMyProfileSubmit).phone_number && {
            phone_number: parsePhoneNumber(
              (profileData as OrganizationAdminMyProfileSubmit).phone_number,
              'US',
            )?.format('E.164') as string,
          }),
          address: userAddress as Address,
        },
        thisUser?.id || 0,
      );

      if (thisUser && thisUser.basic_info !== null) {
        updatedUser = await saveBasicInformation(
          {
            ...(dataWithUpdatedDOB as BasicInfo),
          },
          thisUser?.id || 0,
        );
      } else {
        updatedUser = await createBasicInformation(
          {
            ...(dataWithUpdatedDOB as BasicInfo),
          },
          thisUser?.id || 0,
        );
      }

      if (thisUser && reviewProfile) {
        if (clientTagsLocked) {
          // Tags go back exactly as the user holds them: the server lets an unchanged set through
          // and scopes only the divisions this save changes, so the hidden ones ride along as-is.
          const existingDivisionIds = (thisUser.divisions || []).map((division) => division.id).sort();
          if (!isEqual([...submittedClientDivisionIds].sort(), existingDivisionIds)) {
            await updateUserClientTag(
              thisUser.id,
              {
                client_ids: (thisUser.clients || []).map((cli) => cli.id),
                client_division_ids: submittedClientDivisionIds,
              },
              userInfo.role.name,
            );
          }
        } else if ((profileData as OrganizationAdminMyProfileSubmit).client_tag_ids) {
          const client_ids = submittedClientTagIds;
          const client_division_ids = submittedClientDivisionIds;
          await updateUserClientTag(
            thisUser.id,
            {
              client_ids: client_ids,
              client_division_ids,
            },
            userInfo.role.name,
            { collectorEditingSelf: userInfo.id === thisUser.id },
          );
        }
      }
      const selectedClientTags = submittedClientTagIds;
      const clientTagsSubmitted =
        Boolean((profileData as OrganizationAdminMyProfileSubmit).client_tag_ids) && !clientTagsLocked;
      const nextClients =
        reviewProfile && clientTagsSubmitted
          ? allClientTags.filter((cli) => selectedClientTags.includes(cli.id))
          : thisUser.clients;
      const selectedClientDivisions = submittedClientDivisionIds;
      // Divisions under tags this viewer doesn't administer aren't in the catalog, so a locked
      // save names them from the user's own list.
      const knownDivisions = [
        ...(clientTagsLocked ? thisUser.divisions || [] : []),
        ...allClientTags.flatMap((cli) => cli.divisions || []),
      ];
      const nextDivisions =
        reviewProfile && (clientTagsSubmitted || clientTagsLocked)
          ? selectedClientDivisions
              .map((id) => knownDivisions.find((division) => division.id === id))
              .filter((division): division is { id: number; name: string } => Boolean(division))
              .map((division) => ({ id: division.id, name: division.name }))
          : thisUser.divisions;
      setThisUser({
        ...updatedUser,
        // Organization isn't editable here, so keep what's on record locally.
        organizations: thisUser.organizations,
        organization_admin_profile: {
          ...updatedUser.organization_admin_profile,
        },
        clients: nextClients,
        divisions: nextDivisions,
      });
      setMyProfileForm({
        ...getValues(),
        organizations: getValues('organizations') || [],
        client_tag_ids: getValues('client_tag_ids') || [],
        client_division_ids: getValues('client_division_ids') || [],
      });
      setAvatarAction('');
      setAvatarFile(null);
      setAvatarPayload(null);
      if (!reviewProfile) {
        dispatch(setUser({ userInfo: updatedUser }));
      }

      toast.success('Your changes have been saved');
      setIsEditProfile(false);
    } catch (error: unknown) {
      displayErrorDetails(error);
    }
    setIsSavingProfile(false);
  };

  const updateCurrentAddress = (standardizedAddress: any, postalAddress: any, addressComponents: any): void => {
    const streetNumber = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'street_number',
    );
    const address1 = addressComponents.find((addressComponent: any) => addressComponent.componentType === 'route');
    const state = addressComponents.find(
      (addressComponent: any) => addressComponent.componentType === 'administrative_area_level_1',
    );

    if (postalAddress) {
      setValue('city', postalAddress.locality);
      setValue('zip_code', postalAddress.postalCode);
    }

    if (streetNumber || address1) {
      setValue('address1', `${streetNumber?.componentName?.text || ''} ${address1?.componentName?.text || ''}`.trim());
    }

    if (state) {
      setValue('state', state.componentName?.text || '');
    } else if (standardizedAddress?.state) {
      setValue('state', standardizedAddress.state);
    }
  };

  const onSubmit = async (profileData: OrganizationAdminMyProfileSubmit): Promise<void> => {
    if (hasUnfilledRequiredDivisionGroup(divisionGroups, profileData.client_division_ids || [])) {
      toast.error('Select a division.');
      return;
    }
    await validateAddress(
      profileData,
      getValues as UseFormGetValues<UnifiedProfileSubmit>,
      validationResponseId,
      callSaveProfile,
      setValidationResponseId,
      setShowValidationAddressModal,
      onCloseConfirmationModal,
      updateCurrentAddress,
    );
  };
  const onEditProfile = (): void => {
    setIsEditProfile(true);
  };

  const onCancelEditProfile = (): void => {
    setImage(currentAvatarURL);
    setIsEditProfile(false);
  };

  useEffect(() => {
    return () => {
      if (imageObjectUrlRef.current) {
        URL.revokeObjectURL(imageObjectUrlRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (Object.keys(myProfileForm).length > 1) {
      Object.keys(myProfileForm).forEach((key: unknown) => {
        setValue(
          key as keyof OrganizationAdminMyProfileSubmit,
          myProfileForm[key as keyof OrganizationAdminMyProfileSubmit],
        );
      });
      setRender(true);
      if (!reviewProfile) setLoading(false);
    }
  }, [myProfileForm]);

  const onInvalid = (errorsList: any): void => {
    const error = Object.values(errorsList)[0] as any;
    if (error) {
      toast.error(error.message as string);
    }
  };

  const [selection, setSelection] = React.useState<[number | null, number | null] | null>(null);
  const ref = React.useRef<HTMLInputElement>(null);

  React.useLayoutEffect(() => {
    if (selection && ref.current) {
      [ref.current.selectionStart, ref.current.selectionEnd] = selection;
    }
  }, [selection]);

  const setTextValue = (e: React.ChangeEvent<HTMLInputElement>, name: string): void => {
    const { value } = e.target;
    setValue(name as keyof OrganizationAdminMyProfileSubmit, value, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
    setSelection([e.target.selectionStart, e.target.selectionEnd]);
  };

  const singleClientTagOptions = useMemo(
    () => clientTagOptions.map((option) => ({ id: option.id, value: option.id, label: option.label })),
    [clientTagOptions],
  );

  // A Client Admin never changes a user's client tags, so the tag field is read-only for them and
  // the save sends the tags back as the server holds them; divisions under their own tag stay editable.
  const clientTagsLocked = Boolean(reviewProfile) && userInfo.role.name === UserRoleName.OrganizationAdmin;
  const lockedClientTagOptions = useMemo(() => toLockedSelectOptions(getUserClientTagRefs(thisUser)), [thisUser]);

  const divisionGroups = useMemo(
    () =>
      scopeDivisionGroupsToClientAdmin(
        getClientDivisionOptionGroups(
          allClientTags,
          watchedValues.client_tag_ids || [],
          (thisUser.divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
        ),
        allClientTags,
        divisionScopeActor,
      ),
    [allClientTags, watchedValues.client_tag_ids, thisUser.divisions, divisionScopeActor],
  );

  // Each picker owns only its own tag's ids so the field stays the flat list the payload wants.
  const setGroupDivisionIds = (group: ClientDivisionGroup, ids: string[]): void => {
    const groupIds = new Set(group.options.map((option) => String(option.id)));

    setValue(
      'client_division_ids',
      [...(getValues('client_division_ids') || []).filter((id) => !groupIds.has(String(id))), ...ids],
      { shouldValidate: true },
    );
  };

  useEffect(() => {
    listAllClientTags(reviewProfile ? undefined : userInfo.role.name)
      .then(({ nodes }) => {
        setAllClientTags(nodes);
        setClientTagOptions((nodes || []).map((c) => ({ id: String(c.id), value: c.id, label: c.name })));
      })
      .catch(() => {
        setAllClientTags([]);
        setClientTagOptions([]);
      });
  }, [reviewProfile, userInfo.role.name]);

  const renderEditProfile = (): JSX.Element => {
    return (
      <div className={styles.editProfileContainer}>
        {!reviewProfile && (
          <div className={styles.titleContainer}>
            <span className={styles.title}>Profile</span>
          </div>
        )}
        <div className={styles.editContainer}>
          {!reviewProfile && (
            <ProfileImage
              reviewProfile={reviewProfile}
              id={userId}
              setCurrentAvatarURL={setCurrentAvatarURL}
              setAvatarFile={setAvatarFile}
              setAvatarPayload={setAvatarPayload}
              setAvatarAction={setAvatarAction}
              triggerProfileAvatar={triggerProfileAvatar}
              setImage={setImage}
              image={image}
              errors={errors}
              size="80px"
            />
          )}
          {!reviewProfile && (
            <p className={styles.requiredIndicator}>
              Required <span>*</span>
            </p>
          )}
          <form ref={formRef} className={styles.formContainer} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div className={cn(styles.row, styles.nameRow)}>
              <div className={cn(styles.rowItem, styles.details, styles.midRow)}>
                <div className={styles.caption}>
                  First Name <span>*</span>
                </div>
                <TextField
                  errors={errors}
                  name="first_name"
                  value={watchedValues.first_name || ''}
                  onChange={(e) => setTextValue(e, 'first_name')}
                  wrapperClassName={styles.inputWrapper}
                  inputClass={styles.input}
                />
              </div>
              <div className={cn(styles.rowItem, styles.details, styles.midRow)}>
                <div className={styles.caption}>Middle Name</div>
                <TextField
                  errors={errors}
                  name="middle_name"
                  value={watchedValues.middle_name || ''}
                  onChange={(e) => setTextValue(e, 'middle_name')}
                  wrapperClassName={styles.inputWrapper}
                  inputClass={styles.input}
                />
              </div>
              <div className={cn(styles.rowItem, styles.details, styles.midRow)}>
                <div className={styles.caption}>
                  Last Name <span>*</span>
                </div>
                <TextField
                  errors={errors}
                  name="last_name"
                  value={watchedValues.last_name || ''}
                  onChange={(e) => setTextValue(e, 'last_name')}
                  wrapperClassName={styles.inputWrapper}
                  inputClass={styles.input}
                />
              </div>
            </div>
            <div className={styles.separator} />
            <div className={styles.row}>
              <AddressSection
                register={register}
                errors={errors}
                getValues={getValues}
                setTextValue={setTextValue}
                setValue={setValue}
                showValidationAddressModal={showValidationAddressModal}
                required
              />
            </div>
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <div className={styles.caption}>Phone Number</div>
                <div className={styles.inputWrapper}>
                  <OldPhoneInput
                    value={watchedValues.phone_number as E164Number}
                    id="phone_number"
                    onChange={(val) => {
                      setValue('phone_number', val as string, {
                        shouldDirty: true,
                        shouldTouch: true,
                        shouldValidate: true,
                      });
                    }}
                    inputClass={styles.input}
                    errors={errors}
                    wrapperClassName={styles.inputWrapper}
                    ref={phoneInputRef}
                  />
                </div>
              </div>
            </div>
            <div className={styles.separator} />
            <div className={styles.row}>
              <div className={styles.rowItem}>
                <div className={styles.caption}>
                  Email Address <span>*</span>
                </div>
                <TextField
                  errors={errors}
                  name="email"
                  value={watchedValues.email || ''}
                  onChange={(e) => setTextValue(e, 'email')}
                  wrapperClassName={styles.inputWrapper}
                  inputClass={styles.input}
                />
              </div>
            </div>
            {reviewProfile && clientTagsLocked && (
              <div className={styles.row}>
                <div className={cn(styles.rowItem, styles.details, styles.big)}>
                  <div className={styles.caption}>Client Tag(s)</div>
                  <MultiSelectInputMUI
                    name="locked_client_tag_ids"
                    label=""
                    withId
                    disabled
                    options={lockedClientTagOptions}
                    wrapperClassName={styles.inputWrapper}
                    value={lockedClientTagOptions.map((option) => option.id)}
                  />
                </div>
              </div>
            )}
            {reviewProfile && !clientTagsLocked && (
              <div className={styles.row}>
                <div className={cn(styles.rowItem, styles.details, styles.big)}>
                  <div className={styles.caption}>
                    Client Tag <span>*</span>
                  </div>
                  <MUISelectInput
                    name="client_tag_ids"
                    label=""
                    required
                    options={singleClientTagOptions}
                    wrapperClassName={styles.inputWrapper}
                    errors={errors}
                    value={watchedValues.client_tag_ids?.[0] || ''}
                    onChange={(value: string) => {
                      setValue('client_tag_ids', value ? [value] : [], { shouldValidate: true });
                      const allowedDivisionIds = getAllowedDivisionIds(
                        allClientTags,
                        value ? [value] : [],
                        (thisUser.divisions || []).map((d) => ({ id: String(d.id), value: d.id, label: d.name })),
                      );
                      setValue(
                        'client_division_ids',
                        (getValues('client_division_ids') || []).filter((id) => allowedDivisionIds.has(String(id))),
                        { shouldValidate: true },
                      );
                    }}
                  />
                </div>
              </div>
            )}
            {reviewProfile &&
              (watchedValues.client_tag_ids || []).length > 0 &&
              divisionGroups.map((group) => (
                <div className={styles.row} key={group.clientTagId}>
                  <div className={cn(styles.rowItem, styles.details, styles.big)}>
                    <div className={styles.caption}>
                      {group.clientTagName} - Division(s) {group.required && <span>*</span>}
                    </div>
                    <MultiSelectInputMUI
                      name="client_division_ids"
                      label=""
                      withId
                      required={group.required}
                      options={group.options}
                      wrapperClassName={styles.inputWrapper}
                      errors={errors}
                      value={(watchedValues.client_division_ids || []).filter((id) =>
                        group.options.some((option) => String(option.id) === String(id)),
                      )}
                      onChange={(ids: string[]) => setGroupDivisionIds(group, ids)}
                    />
                  </div>
                </div>
              ))}
            <div className={`${styles.actionBtnContainer} ${styles.splitActionBtnContainer}`}>
              {!reviewProfile && (
                <Button type="button" className={styles.cancel} onClick={onCancelEditProfile}>
                  Cancel
                </Button>
              )}
              <Button
                type="submit"
                className={styles.submit}
                disabled={(isEqual(myProfileForm, watchedValues) && isEqual(avatarAction, '')) || isSavingProfile}
              >
                Save
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const fullAddress = useMemo(() => {
    if (thisUser?.address) {
      const { address1, address2, city, state, zip_code: zipCode } = thisUser.address;
      return `${address1}${address2 ? ` , ${address2}` : ''}, ${city}, ${state} ${zipCode}`;
    }

    return ' ';
  }, [thisUser]);

  const displayPhoneNumber = useMemo(() => {
    if (thisUser?.phone_number) {
      const phoneNumber = parsePhoneNumber(thisUser?.phone_number, 'US');
      if (phoneNumber) {
        return phoneNumber.formatNational();
      }
    }

    return '';
  }, [thisUser]);

  const userOrganization = useMemo(() => {
    if (thisUser?.organizations) {
      return thisUser?.organizations
        .map((organization) => {
          return organization.name;
        })
        .join(', ');
    }
    return ' ';
  }, [thisUser]);

  const userClient = useMemo(() => {
    if (thisUser?.clients) {
      return thisUser?.clients
        .map((client) => {
          return client.name;
        })
        .join(', ');
    }
    return ' ';
  }, [thisUser]);

  const displayEmail = useMemo(() => {
    if (thisUser?.email) {
      return thisUser.email;
    }

    return '';
  }, [thisUser]);

  const renderReadOnlyProfile = (): JSX.Element => {
    return (
      <div className={styles.readOnlyProfileContainer}>
        <div className={styles.titleContainer}>
          <span className={styles.title}>Profile</span>
          {!(
            userInfo.role.name === UserRoleName.Admin &&
            (roleName === UserRoleName.SuperAdmin || roleName === UserRoleName.Admin)
          ) && (
            <button className={styles.editBtn} type="button" onClick={onEditProfile}>
              Edit
              <FiEdit className={styles.editIcon} />
            </button>
          )}
        </div>
        <div className={styles.detailsContainer}>
          {!reviewProfile && (
            <ProfileImage
              reviewProfile={reviewProfile}
              id={userId}
              image={image}
              setImage={setImage}
              className={styles.profileImg}
              size="80px"
              isUpload={false}
            />
          )}
          <div className={styles.readOnlyItem}>
            <div className={styles.caption}>Name</div>
            <div className={styles.value}>
              {thisUser?.basic_info?.first_name} {thisUser?.basic_info?.last_name}
            </div>
          </div>
        </div>
        {thisUser?.address && (
          <div className={styles.detailsContainer}>
            <div className={styles.readOnlyItem}>
              <div className={styles.caption}>Address</div>
              <div className={styles.value}>{fullAddress}</div>
            </div>
          </div>
        )}
        {thisUser?.phone_number && (
          <div className={styles.detailsContainer}>
            <div className={styles.readOnlyItem}>
              <div className={styles.caption}>Phone Number</div>
              <div className={styles.value}>{displayPhoneNumber}</div>
            </div>
          </div>
        )}
        {thisUser?.email && (
          <div className={styles.detailsContainer}>
            <div className={styles.readOnlyItem}>
              <div className={styles.caption}>Email</div>
              <div className={styles.value}>{displayEmail}</div>
            </div>
          </div>
        )}
        {reviewProfile && (
          <div className={styles.detailsContainer}>
            <div className={styles.readOnlyItem}>
              <div className={styles.caption}>Organization</div>
              <div className={styles.value}>{userOrganization}</div>
            </div>
          </div>
        )}
        {reviewProfile && (
          <div className={styles.detailsContainer}>
            <div className={styles.readOnlyItem}>
              <div className={styles.caption}>Client Tags</div>
              <div className={styles.value}>{userClient}</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const clientTagChips = useMemo(() => {
    if ((thisUser?.clients?.length || 0) > 0) {
      return thisUser?.clients?.map((client) => client.name);
    }
    return [];
  }, [thisUser]);

  if (!render) {
    return <Loading fullScreen />;
  }

  if (loading) {
    return <Loading fullScreen />;
  }

  const shouldDisplayEditProfile = reviewProfile || isEditProfile;

  if (!reviewProfile) {
    return (
      <Container
        patientDashboard
        childClassName={styles.dashboard}
        isSidenavVisible
        childrenStyle={{ alignItems: 'flex-start' }}
      >
        <div className={newProfileStyles.profileContainer}>
          <form ref={formRef} className={newProfileStyles.formWrapper} onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <div className={newProfileStyles.avatarRow}>
              <ProfileImage
                reviewProfile={false}
                id={undefined}
                setCurrentAvatarURL={setCurrentAvatarURL}
                setAvatarFile={setAvatarFile}
                setAvatarPayload={setAvatarPayload}
                setAvatarAction={setAvatarAction}
                triggerProfileAvatar={triggerProfileAvatar}
                setImage={setImage}
                image={image}
                errors={errors}
                size="72px"
                isUpload={false}
              />
              <MuiButton
                variant="text"
                component="label"
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '32px',
                  color: 'var(--primary-color)',
                }}
              >
                Upload profile picture
                <input
                  type="file"
                  hidden
                  accept=".png,.jpg,.jpeg,.jfif,.pjpeg,.pjp,.heic"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      setAvatarAction('Upload');
                      setImage(() => {
                        if (imageObjectUrlRef.current) {
                          URL.revokeObjectURL(imageObjectUrlRef.current);
                        }
                        const objectUrl = URL.createObjectURL(file);
                        imageObjectUrlRef.current = objectUrl;
                        return objectUrl;
                      });
                      setAvatarPayload({ files: [{ filename: file.name, mimetype: file.type }] });
                      setAvatarFile(file);
                    }
                  }}
                />
              </MuiButton>
            </div>

            <div className={newProfileStyles.nameRow}>
              <CustomTextField
                name="first_name"
                label="First name"
                required
                value={watchedValues.first_name || ''}
                onChange={(e) => setTextValue(e, 'first_name')}
                errors={errors}
              />
              <CustomTextField
                name="middle_name"
                label="Middle name (optional)"
                value={watchedValues.middle_name || ''}
                onChange={(e) => setTextValue(e, 'middle_name')}
                errors={errors}
              />
              <CustomTextField
                name="last_name"
                label="Last name"
                required
                value={watchedValues.last_name || ''}
                onChange={(e) => setTextValue(e, 'last_name')}
                errors={errors}
              />
            </div>

            <AddressSection
              register={register}
              errors={errors}
              getValues={getValues}
              setTextValue={setTextValue}
              setValue={setValue}
              showValidationAddressModal={showValidationAddressModal}
              required
            />

            <MuiPhoneInput
              name="phone_number"
              label="Phone number"
              value={watchedValues.phone_number as E164Number}
              onChange={(val) =>
                setValue('phone_number', val as string, {
                  shouldDirty: true,
                  shouldTouch: true,
                  shouldValidate: true,
                })
              }
              errors={errors}
              ref={phoneInputRef}
            />

            <CustomTextField
              name="email"
              label="Email address"
              required
              value={watchedValues.email || ''}
              onChange={(e) => setTextValue(e, 'email')}
              errors={errors}
            />

            <div className={caStyles.clientTagsContainer}>
              <div className={caStyles.clientTagsLabel}>Client tags</div>
              <div className={caStyles.clientTagsChips}>
                {clientTagChips?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    variant="outlined"
                    size="medium"
                    sx={{
                      borderRadius: '100px',
                      borderColor: 'var(--primary-color)',
                      color: 'var(--primary-color)',
                      pointerEvents: 'none',
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: '13px',
                    }}
                  />
                ))}
                {clientTagChips?.length === 0 && (
                  <span className={caStyles.clientTagsEmpty}>No client tags assigned</span>
                )}
              </div>
            </div>

            <div className={newProfileStyles.buttonRow}>
              <MuiButton
                type="submit"
                variant="contained"
                className={newProfileStyles.saveButton}
                disabled={(isEqual(myProfileForm, watchedValues) && isEqual(avatarAction, '')) || isSavingProfile}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '32px',
                  px: '22px',
                  py: '8px',
                  backgroundColor: 'var(--primary-color)',
                  '&:hover': { backgroundColor: '#245fa0' },
                  '&.Mui-disabled': { backgroundColor: '#bdbdbd', color: '#fff' },
                }}
              >
                Save changes
              </MuiButton>
              <MuiButton
                type="button"
                variant="text"
                onClick={() => setChangePasswordOpen(true)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '15px',
                  borderRadius: '32px',
                  color: '#d32f2f',
                  py: '8px',
                  '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' },
                }}
              >
                Reset password
              </MuiButton>
            </div>
          </form>
        </div>

        <ChangePasswordModal
          isOpen={changePasswordOpen}
          onClose={() => setChangePasswordOpen(false)}
          email={thisUser.email || userInfo?.email || ''}
        />
        {showModal && (
          <UnsavedChangesModal
            isOpen={showModal}
            onConfirm={confirmNavigation}
            saveChange={saveChanges}
            loading={isSavingProfile}
            hideModal={hideModal}
          />
        )}
      </Container>
    );
  }

  return (
    <div className={`${styles.profileContainer} ${reviewProfile ? styles.profileContainerReview : ''}`}>
      {!reviewProfile && (
        <>
          <img className={styles.bkgTopImage} src={BlueCircle} alt="profile" />
          <img className={styles.bkgBottomImage} src={GrayCircle} alt="profile" />
          <Sidenav isVisibleOnMobile />
        </>
      )}
      <div className={styles.contentContainer}>
        {reviewProfile && (
          <ModernReviewProfileHeader
            thisUser={thisUser}
            setThisUser={setThisUser}
            userId={userId}
            resetEmailSent={resetEmailSent}
            setResetPasswordConfirmation={setResetPasswordConfirmation}
            setChangeUserStatusFlag={setChangeUserStatusFlag}
            setCurrentAvatarURL={setCurrentAvatarURL}
            setAvatarFile={setAvatarFile}
            setAvatarPayload={setAvatarPayload}
            setAvatarAction={setAvatarAction}
            triggerProfileAvatar={triggerProfileAvatar}
            setImage={setImage}
            image={image}
            errors={errors}
            roleName={userInfo.role.name}
          />
        )}
        {shouldDisplayEditProfile ? renderEditProfile() : renderReadOnlyProfile()}
      </div>
      {showModal && (
        <UnsavedChangesModal
          isOpen={showModal}
          onConfirm={confirmNavigation}
          saveChange={saveChanges}
          loading={isSavingProfile}
          hideModal={hideModal}
        />
      )}
      {resetPasswordConfirmation.isOpen && (
        <ResetPasswordModal
          setOpenModal={setResetPasswordConfirmation}
          disableButton={disableButton}
          userId={Number(userId)}
          userEmail={thisUser.email}
          userName={thisUser.full_name}
          isDeactivated={thisUser.status === 'Deactivated'}
          userState={resetPasswordConfirmation.userState}
        />
      )}
      {changeUserStatusFlag && (
        <ChangeUserStatusModal
          setOpenModal={setChangeUserStatusFlag}
          disableButton={disableDeleteButton}
          userId={Number(userId)}
          userName={thisUser.full_name}
          status={changeUserStatusFlag}
          thisUser={thisUser}
          setThisUser={setThisUser}
        />
      )}
    </div>
  );
};
MyProfile.defaultProps = {
  reviewProfile: false,
};
export default MyProfile;
